import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faEdit,
  faArrowLeft,
  faSpinner,
  faSearchPlus
} from '@fortawesome/free-solid-svg-icons';
import actionService from '../../services/actionService';
import targetService from '../../services/targetService';
import projectService from '../../services/projectService';
import siemCorrelationService from '../../services/siemCorrelationService';
import ActionDetailView from './ActionDetailView';
import ActionDetailEdit from './ActionDetailEdit';

const ActionDetailModal = ({ action, isOpen, onClose, projectId, onActionUpdated }) => {
  const { t, formatDate } = useLanguage();
  const { darkMode: isDarkMode } = useTheme();

  const [mode, setMode] = useState('view'); // 'view' | 'edit'
  const [currentAction, setCurrentAction] = useState(null);
  const [editData, setEditData] = useState(null); // { targets, teamMembers }
  const [loadingEditData, setLoadingEditData] = useState(false);
  const [saving, setSaving] = useState(false);
  const [correlationLoading, setCorrelationLoading] = useState(false);
  const editDataCache = useRef(null); // cache per projectId
  const editingRef = useRef(false);
  const pollRef = useRef(null);

  // Sync currentAction from prop, but don't override during edit
  useEffect(() => {
    if (action && !editingRef.current) {
      setCurrentAction(action);
      setMode('view');
    }
  }, [action]);

  // Track editing state in ref for race-condition protection
  useEffect(() => {
    editingRef.current = mode === 'edit';
  }, [mode]);

  // Reset when modal closes
  useEffect(() => {
    if (!isOpen) {
      setMode('view');
      editingRef.current = false;
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      setCorrelationLoading(false);
    }
  }, [isOpen]);

  const resolvedProjectId = projectId || currentAction?.project?._id || currentAction?.project || '';

  const fetchEditData = useCallback(async () => {
    if (!resolvedProjectId) return;

    // Use cached data if available for same project
    if (editDataCache.current?.projectId === resolvedProjectId) {
      setEditData(editDataCache.current.data);
      return;
    }

    setLoadingEditData(true);
    try {
      const [targets, project] = await Promise.all([
        targetService.getTargetsByProject(resolvedProjectId),
        projectService.getProject(resolvedProjectId)
      ]);
      const data = { targets, teamMembers: project.team || [] };
      editDataCache.current = { projectId: resolvedProjectId, data };
      setEditData(data);
    } catch (err) {
      console.error('Error fetching edit data:', err);
      setEditData({ targets: [], teamMembers: [] });
    } finally {
      setLoadingEditData(false);
    }
  }, [resolvedProjectId]);

  const handleEditClick = async () => {
    // Re-fetch the action to get clean field names
    if (currentAction?._id) {
      try {
        const freshAction = await actionService.getAction(currentAction._id);
        setCurrentAction(freshAction);
      } catch (err) {
        console.error('Error fetching fresh action:', err);
        // Continue with existing action data
      }
    }
    await fetchEditData();
    setMode('edit');
  };

  const handleSave = async (formData) => {
    if (!currentAction?._id) return;
    setSaving(true);
    try {
      await actionService.updateAction(currentAction._id, formData);
      // Re-fetch for fresh data
      const updatedAction = await actionService.getAction(currentAction._id);
      setCurrentAction(updatedAction);
      setMode('view');
      if (onActionUpdated) {
        onActionUpdated(updatedAction);
      }
    } catch (err) {
      console.error('Error updating action:', err);
      alert(err.response?.data?.message || t('errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setMode('view');
  };

  const handleCheckSIEM = async () => {
    if (!currentAction?._id || correlationLoading) return;
    setCorrelationLoading(true);

    try {
      await siemCorrelationService.triggerCorrelation(currentAction._id);

      // Poll status every 3 seconds
      const startTime = Date.now();
      pollRef.current = setInterval(async () => {
        try {
          const statusData = await siemCorrelationService.getStatus(currentAction._id);
          if (statusData.status === 'completed' || statusData.status === 'failed' || statusData.status === 'partial') {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setCorrelationLoading(false);
            // Re-fetch the full action to get updated data
            const updatedAction = await actionService.getAction(currentAction._id);
            setCurrentAction(updatedAction);
            if (onActionUpdated) onActionUpdated(updatedAction);
          }
          // Safety timeout: stop after 120s
          if (Date.now() - startTime > 120000) {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setCorrelationLoading(false);
          }
        } catch (err) {
          console.error('Error polling correlation status:', err);
          clearInterval(pollRef.current);
          pollRef.current = null;
          setCorrelationLoading(false);
        }
      }, 3000);
    } catch (err) {
      console.error('Error triggering SIEM correlation:', err);
      setCorrelationLoading(false);
    }
  };

  const handleClose = () => {
    if (mode === 'edit' && !saving) {
      if (window.confirm(t('actions.unsavedChangesWarning') || 'You have unsaved changes. Discard them?')) {
        setMode('view');
        onClose();
      }
    } else {
      onClose();
    }
  };

  if (!isOpen || !currentAction) return null;

  const title = currentAction.title || currentAction.name || currentAction.action || t('actions.actionTitle');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={handleClose}>
      <div
        className={`rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`sticky top-0 z-10 flex items-center justify-between p-4 border-b flex-shrink-0 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center gap-3 min-w-0 flex-1">
            {mode === 'edit' && (
              <button
                onClick={handleCancelEdit}
                className={`p-1.5 rounded-full transition-colors flex-shrink-0 ${isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
                title={t('common.back')}
              >
                <FontAwesomeIcon icon={faArrowLeft} />
              </button>
            )}
            <h2 className={`text-lg font-bold truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {mode === 'edit' ? (t('actions.editingAction') || 'Editing Action') : title}
            </h2>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Check SIEM button - only in view mode */}
            {mode === 'view' && currentAction._id && (
              <button
                onClick={handleCheckSIEM}
                className={`p-2 rounded-full transition-colors ${isDarkMode ? 'hover:bg-gray-700 text-teal-400' : 'hover:bg-teal-50 text-teal-600'}`}
                title={t('siemCorrelation.checkSIEM') !== 'siemCorrelation.checkSIEM' ? t('siemCorrelation.checkSIEM') : 'Check SIEM'}
                disabled={correlationLoading}
              >
                {correlationLoading ? (
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                ) : (
                  <FontAwesomeIcon icon={faSearchPlus} />
                )}
              </button>
            )}
            {/* Edit button - only in view mode */}
            {mode === 'view' && resolvedProjectId && currentAction._id && (
              <button
                onClick={handleEditClick}
                className={`p-2 rounded-full transition-colors ${isDarkMode ? 'hover:bg-gray-700 text-blue-400' : 'hover:bg-blue-50 text-blue-600'}`}
                title={t('common.edit')}
                disabled={loadingEditData}
              >
                {loadingEditData ? (
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                ) : (
                  <FontAwesomeIcon icon={faEdit} />
                )}
              </button>
            )}
            {/* Close button */}
            <button
              onClick={handleClose}
              className={`p-2 rounded-full transition-colors ${isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {mode === 'view' ? (
            <ActionDetailView
              action={currentAction}
              isDarkMode={isDarkMode}
              t={t}
              formatDate={formatDate}
              onRecheck={handleCheckSIEM}
            />
          ) : loadingEditData ? (
            <div className="flex items-center justify-center py-20">
              <FontAwesomeIcon icon={faSpinner} className="animate-spin text-blue-500 text-2xl mr-3" />
              <span className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>
                {t('actions.loadingEditForm') || 'Loading edit form...'}
              </span>
            </div>
          ) : (
            <ActionDetailEdit
              action={currentAction}
              projectId={resolvedProjectId}
              isDarkMode={isDarkMode}
              availableTargets={editData?.targets || []}
              teamMembers={editData?.teamMembers || []}
              onSave={handleSave}
              onCancel={handleCancelEdit}
              t={t}
              saving={saving}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ActionDetailModal;
